import React, { useEffect, useState, useCallback } from 'react';
import { Card, Row, Col, Tag, Button, message, Empty, Segmented, Spin, Space, Popconfirm, Input, Modal } from 'antd';
import { EyeOutlined, PrinterOutlined, CheckCircleOutlined, PlayCircleOutlined, CloseCircleOutlined, RollbackOutlined, EditOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { glassesOrderService } from '../../services';
import { printGlassesPrescription } from '../../components/Print/printGlassesPrescription';
import { EYES, POWER_COLUMNS, ORDER_STATUS_COLORS, orderStatusLabel, translate } from '../../utils/glasses';

// What the optician does next with an order in each status: [status to move
// to, translation key, fallback, icon]. The first is the way forward.
const FORWARD = {
    new: ['in_progress', 'glassesStart', 'Start making', <PlayCircleOutlined />],
    in_progress: ['ready', 'glassesMarkReady', 'Mark ready', <CheckCircleOutlined />],
    ready: ['delivered', 'glassesMarkDelivered', 'Mark delivered', <CheckCircleOutlined />]
};

// One step back, for a misclick.
const BACK = { in_progress: 'new', ready: 'in_progress', cancelled: 'new' };

const Orders = () => {
    const { t, i18n } = useTranslation();
    const [view, setView] = useState('open');
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [busyId, setBusyId] = useState(null);
    // The order whose note is being written, and the note as typed so far.
    const [noteTarget, setNoteTarget] = useState(null);
    const [noteText, setNoteText] = useState('');

    const tr = (key, fallback) => translate(t, key, fallback);

    const fetchOrders = useCallback(async () => {
        setLoading(true);
        try {
            const res = await glassesOrderService.getOrders(view);
            setOrders(res.data || []);
        } catch (error) {
            message.error(error.response?.data?.message || t('error'));
        } finally {
            setLoading(false);
        }
    }, [view, t]);

    useEffect(() => {
        fetchOrders();
    }, [fetchOrders]);

    const update = async (order, data) => {
        setBusyId(order._id);
        try {
            await glassesOrderService.updateStatus(order._id, data);
            message.success(t('success'));
            fetchOrders();
            return true;
        } catch (error) {
            message.error(error.response?.data?.message || t('error'));
            return false;
        } finally {
            setBusyId(null);
        }
    };

    const saveNote = async () => {
        if (await update(noteTarget, { opticianNote: noteText })) setNoteTarget(null);
    };

    const print = (order) => {
        const opened = printGlassesPrescription({
            spec: order.spec || {},
            patient: order.patientId || { name: order.patientName, phoneNumber: order.patientPhone },
            doctorInfo: order.doctorId || { name: order.doctorName },
            date: order.createdAt,
            lang: i18n.language,
            t
        });
        if (!opened) message.warning(tr('popupBlocked', 'Allow pop-ups for this site to print'));
    };

    const actionsFor = (order) => {
        const busy = busyId === order._id;
        const forward = FORWARD[order.status];
        const back = BACK[order.status];

        return [
            <Button type="link" icon={<PrinterOutlined />} onClick={() => print(order)} key="print">
                {t('print')}
            </Button>,
            forward && (
                <Popconfirm
                    key="forward"
                    title={`${tr(forward[1], forward[2])}?`}
                    onConfirm={() => update(order, { status: forward[0] })}
                >
                    <Button type="link" icon={forward[3]} loading={busy}>
                        {tr(forward[1], forward[2])}
                    </Button>
                </Popconfirm>
            ),
            back && (
                <Popconfirm
                    key="back"
                    title={`${tr('glassesMoveBack', 'Move back to')} ${orderStatusLabel(t, back)}?`}
                    onConfirm={() => update(order, { status: back })}
                >
                    <Button type="link" icon={<RollbackOutlined />} loading={busy} />
                </Popconfirm>
            ),
            forward && (
                <Popconfirm
                    key="cancel"
                    title={`${tr('glassesCancelOrder', 'Cancel order')}?`}
                    okButtonProps={{ danger: true }}
                    onConfirm={() => update(order, { status: 'cancelled' })}
                >
                    <Button type="link" danger icon={<CloseCircleOutlined />} loading={busy} />
                </Popconfirm>
            )
        ].filter(Boolean);
    };

    return (
        <div className="page-container">
            <Row align="middle" justify="space-between" style={{ marginBottom: 16 }} gutter={[8, 8]}>
                <Col><h1 style={{ margin: 0 }}><EyeOutlined /> {tr('glassesOrders', 'Glasses orders')}</h1></Col>
                <Col>
                    <Segmented
                        value={view}
                        onChange={setView}
                        options={[
                            { label: tr('glassesOpen', 'Open'), value: 'open' },
                            { label: orderStatusLabel(t, 'new'), value: 'new' },
                            { label: orderStatusLabel(t, 'in_progress'), value: 'in_progress' },
                            { label: orderStatusLabel(t, 'ready'), value: 'ready' },
                            { label: orderStatusLabel(t, 'delivered'), value: 'delivered' },
                            { label: t('all'), value: 'all' }
                        ]}
                    />
                </Col>
            </Row>

            <Spin spinning={loading}>
                {orders.length === 0 ? (
                    <Empty description={tr('noGlassesOrders', 'No glasses orders')} />
                ) : (
                    <Row gutter={[16, 16]}>
                        {orders.map((order) => {
                            const spec = order.spec || {};
                            const phone = order.patientId?.phoneNumber || order.patientPhone;

                            return (
                                <Col xs={24} md={12} xl={8} key={order._id}>
                                    <Card
                                        title={
                                            <Space direction="vertical" size={0}>
                                                <span>{order.patientId?.name || order.patientName}</span>
                                                <small style={{ color: '#888', fontWeight: 'normal' }}>
                                                    {t('byDoctor')} {order.doctorId?.name || order.doctorName}
                                                    {phone ? ` · ${phone}` : ''}
                                                </small>
                                            </Space>
                                        }
                                        extra={<Tag color={ORDER_STATUS_COLORS[order.status]}>{orderStatusLabel(t, order.status)}</Tag>}
                                        actions={actionsFor(order)}
                                    >
                                        {/* The powers read left to right in every language, as
                                            they are written on the prescription pad. */}
                                        <table style={{ width: '100%', borderCollapse: 'collapse', direction: 'ltr', textAlign: 'center' }}>
                                            <thead>
                                                <tr>
                                                    <th />
                                                    {POWER_COLUMNS.map(([suffix, label]) => (
                                                        <th key={suffix} style={{ color: '#888', fontSize: 12, fontWeight: 500 }}>{label}</th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {EYES.map(([eye, labelKey, fallback]) => (
                                                    <tr key={eye} style={{ borderTop: '1px solid #f0f0f0' }}>
                                                        <td style={{ textAlign: 'start', padding: '6px 0', color: '#555' }}>{tr(labelKey, fallback)}</td>
                                                        {POWER_COLUMNS.map(([suffix]) => (
                                                            <td key={suffix} style={{ fontWeight: 600, padding: '6px 4px' }}>
                                                                {spec[`${eye}${suffix}`] || '-'}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>

                                        <Space wrap style={{ marginTop: 8 }}>
                                            {spec.pd && <Tag>{tr('pupillaryDistance', 'PD (mm)')}: {spec.pd}</Tag>}
                                            {spec.lensType && <Tag color="blue">{spec.lensType}</Tag>}
                                        </Space>
                                        {spec.note && <div style={{ marginTop: 8, whiteSpace: 'pre-wrap' }}>{spec.note}</div>}

                                        <div style={{ marginTop: 8 }}>
                                            <Button
                                                type="link"
                                                size="small"
                                                icon={<EditOutlined />}
                                                style={{ padding: 0 }}
                                                onClick={() => {
                                                    setNoteTarget(order);
                                                    setNoteText(order.opticianNote || '');
                                                }}
                                            >
                                                {tr('opticianNote', 'Note for the doctor')}
                                            </Button>
                                            {order.opticianNote && (
                                                <div style={{ color: '#888', whiteSpace: 'pre-wrap' }}>{order.opticianNote}</div>
                                            )}
                                        </div>

                                        <div style={{ marginTop: 8, color: '#aaa', fontSize: 12 }}>
                                            {order.createdAt ? new Date(order.createdAt).toLocaleString() : ''}
                                        </div>
                                    </Card>
                                </Col>
                            );
                        })}
                    </Row>
                )}
            </Spin>

            <Modal
                title={tr('opticianNote', 'Note for the doctor')}
                open={Boolean(noteTarget)}
                onCancel={() => setNoteTarget(null)}
                onOk={saveNote}
                okText={t('save')}
                confirmLoading={Boolean(noteTarget) && busyId === noteTarget._id}
                destroyOnClose
            >
                <Input.TextArea
                    autoSize={{ minRows: 3, maxRows: 8 }}
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                />
            </Modal>
        </div>
    );
};

export default Orders;
